import * as flvjs from 'flv.js';
import { BehaviorSubject, Subject } from 'rxjs';

import { Platform } from '@angular/cdk/platform';

import { FsDataDogRumService } from '@lib/data-dog-rum';
import { SnPlayerState } from '@lib/player/common';

import { SnVideoPlayerType } from '../video-player.enums';
import { SnVideoPlayerReference } from '../video-player.reference';
import { PLAYERS_REFERENCE } from './players.reference.constants';
import { emitStreamError } from './stream-error.utils';

export class FsFlvPlayerReference extends SnVideoPlayerReference {
  public player: flvjs.default.Player;
  private _playPromise: Promise<void>;

  private _callback: Function;

  private _isPlaying: boolean;

  private _src: string;

  constructor(
    playerElement: HTMLElement,
    videoElement: HTMLVideoElement,
    url: string,
    platform: Platform,
    state$: BehaviorSubject<SnPlayerState>,
    currentTime$: BehaviorSubject<number>,
    bufferTime$: BehaviorSubject<number>,
    totalTime$: BehaviorSubject<number>,
    volume$: BehaviorSubject<number>,
    played$: BehaviorSubject<null>,
    loaded$: Subject<boolean>,
    ended$: Subject<void>,
    autoplay: boolean,
    callback: Function,
    bearer?: string,
    private readonly _datadogService?: FsDataDogRumService,
    private readonly camId?: string,
  ) {
    super(
      SnVideoPlayerType.FLV,
      playerElement,
      platform,
      state$,
      currentTime$,
      bufferTime$,
      totalTime$,
      volume$,
      played$,
      loaded$,
      ended$,
    );
    this._callback = callback;
    this._src = url;
    this._init(videoElement, url, autoplay, bearer);
  }

  public destroy(): void {
    this.player.pause();
    this.player.unload();
    this.player.detachMediaElement();
    this.player.destroy();
    this.player = null;

    super.destroy();
  }

  public play(): void {
    this._isPlaying = true;
    this._playPromise = this.videoElement.play();
  }

  public pause(): void {
    this._isPlaying = false;
    const playPromise = this._playPromise;
    if (playPromise !== undefined) {
      playPromise.then(() => {
        this.videoElement.pause();
        if (this._playPromise === playPromise) {
          this._playPromise = undefined;
        }
      });
    } else {
      this.videoElement.pause();
    }
  }

  private _init(element: HTMLVideoElement, url: string, autoplay: boolean, bearer?: string): void {
    if (flvjs.default.isSupported()) {
      this.videoElement = element;

      const playerConfig: flvjs.default.MediaDataSource = {
        type: 'flv',
        url,
      };

      const playerOptions: flvjs.default.Config = {};

      // Only add Authorization header for EEN streams (when bearer token is provided)
      if (bearer) {
        playerOptions.headers = {
          Authorization: bearer,
        };
      }

      this.player = flvjs.default.createPlayer(playerConfig, playerOptions);
      this.player.attachMediaElement(this.videoElement);

      this.player.on(
        flvjs.default.Events.ERROR,
        (errorType: string, errorDetail: string | flvjs.default.LoaderErrorMessage | unknown) => {
          if (this._datadogService && this.camId) {
            const isFatal =
              errorType === flvjs.default.ErrorTypes.NETWORK_ERROR ||
              errorType === flvjs.default.ErrorTypes.MEDIA_ERROR;
            emitStreamError({
              datadogService: this._datadogService,
              cameraId: this.camId,
              protocol: PLAYERS_REFERENCE.PROTOCOL.FLV,
              sourceUi: PLAYERS_REFERENCE.SOURCE_UI.FLV,
              errorType: errorType || 'unknown',
              rawError: JSON.stringify({ errorType, errorDetail }),
              errorCode:
                typeof errorDetail === 'string'
                  ? errorDetail
                  : typeof errorDetail === 'object' && errorDetail !== null && 'code' in errorDetail
                  ? String((errorDetail as flvjs.default.LoaderErrorMessage).code)
                  : 'UNKNOWN',
              isLive: this.live || false,
              isFatal,
              streamUrl: this._src,
              protocolSpecificFields: {
                flv_error_type: errorType,
                flv_error_details: errorDetail,
              },
            });
          }
          console.error('FLV PLAYER ERROR:', errorType, errorDetail);
        },
      );

      this.player.load();
      if (autoplay || this._isPlaying) {
        this._playPromise = this.videoElement.play();
        this._playPromise.then(() => this._callback());
      } else {
        this._callback();
      }
    }
  }
}
